// /* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import { useTicketGetByIdMutation } from '../Ticket.api'
import { useAppSelector } from '../../../store/Store'
import { selectTicket, selectTicketError, selectTicketLoading } from '../Ticket.slice'
import { CriticalityLevel, ITicketDto } from '../Ticket.model'
import { CustomInputRenderer } from '../../../../common/component/CustomInputRenderer'
import { Spinner } from '../../../../common/component/Spinner'
import { parseLocalDate } from '../../../../util/Locale'
import { IconLabelContent } from '../../../../common/component/IconLabelContent'
import classNames from 'classnames'

interface Props {
  ticketId: string
}

export const TicketEmbedered: FC<Props> = ({ticketId}: Props) => {
  const intl = useIntl()
  const data = useAppSelector(selectTicket)
  const isLoading = useAppSelector(selectTicketLoading)
  const error = useAppSelector(selectTicketError)
  const [getById] = useTicketGetByIdMutation()

  useEffect(() => {
    const fetchTicket = async (ticketId) => {
      if (!data || !data.ticket || data.ticket.id !== ticketId) {
        await getById({id: ticketId})
      }
    }
    fetchTicket(ticketId)
  }, [])

  const buildDescriptionLine = (data: ITicketDto) => {
    let description = ``
    if (data.statusCode) {
      description += description
        ? ` | ${intl.formatMessage({id: `DROPDOWN.TICKET.STATUS.${data.statusCode}`})}`
        : `| ${intl.formatMessage({id: `DROPDOWN.TICKET.STATUS.${data.statusCode}`})}`
    }
    return description
  }

  const buildAssetDescriptionLine = (data: ITicketDto) => {
    let description = ``
    if (data.asset) {
      description += description
        ? ` | ${data.asset.family} | ${data.asset.area} | ${data.asset.brand} | ${data.asset.model} | ${data.asset.serial}`
        : `${data.asset.family} | ${data.asset.area} | ${data.asset.brand} | ${data.asset.model} | ${data.asset.serial}`
    }

    return description
  }

  const buildClientDescriptionLine = (data: ITicketDto) => {
    let description = ``
    if (data.client) {
      description += description ? ` | ${data.client.displayName}` : `${data.client.displayName}`
    }
    if (data.reportedBy) {
      description += description ? ` | ${data.reportedBy.email}` : `${data.reportedBy.email}`
    }
    return description
  }

  return (
    <>
      <CustomInputRenderer
        label={intl.formatMessage({id: 'UTIL.TICKET'})}
        object={
          !data && isLoading ? (
            <Spinner />
          ) : data && data.ticket ? (
            <div className='d-flex flex-stack py-2 bg-light-primary'>
              <div className='d-flex flex-stack'>
                <div className='d-flex align-items-center'>
                  <div className='ms-2'>
                    <div className='fs-7 cursor-pointer'>
                      <Link className='' to={`/tickets/${data.ticket.id}`} target='_blank'>
                        <span className='text-primary mb-2'>
                          {`${parseLocalDate(
                            data.ticket.createdAt,
                            intl.formatMessage({id: 'I18N.LOCALE'})
                          )}`}
                        </span>
                        <span className='text-gray-600 ms-2 me-2 mb-2'>-</span>
                        <span className='fw-bolder mb-2'>{`${data.ticket.title}`}</span>
                        <span className='text-gray-600 ms-2 me-2 mb-2'>-</span>
                        <span className='text-primary mb-2'>{data.ticket.sequence}</span>
                      </Link>
                    </div>

                    <div className='d-flex justify-content-start'>
                      <div className='d-flex align-items-center fs-8'>
                        <span
                          className={classNames('me-1', {
                            'text-danger': data.ticket.criticalityCode === CriticalityLevel.SEVERE,
                            'text-warning': data.ticket.criticalityCode === CriticalityLevel.HIGH,
                            'text-success':
                              data.ticket.criticalityCode === CriticalityLevel.MODERATE,
                            'text-primary': data.ticket.criticalityCode === CriticalityLevel.LOW,
                          })}
                        >
                          {intl.formatMessage({
                            id: `TICKET.CRITICALITY.${data.ticket.criticalityCode}`,
                          })}
                        </span>
                        <span className='text-gray-600'>{buildDescriptionLine(data.ticket)}</span>
                      </div>
                    </div>

                    <div className='d-flex justify-content-start'>
                      <div className='d-flex align-items-center fs-8'>
                        <span className='text-gray-600'>
                          {buildClientDescriptionLine(data.ticket)}
                        </span>
                      </div>
                    </div>

                    <div className='d-flex justify-content-start'>
                      <div className='d-flex align-items-center fs-8'>
                        <span className='text-gray-600'>
                          {buildAssetDescriptionLine(data.ticket)}
                        </span>
                      </div>
                    </div>

                    <div className='d-flex justify-content-start align-items-center'>
                      {/* <IconLabelContent
                          title={`${intl.formatMessage({
                            id: 'UTIL.CRITICALITY_LEVEL',
                          })}: ${intl.formatMessage({
                            id: `TICKET.CRITICALITY.${data.ticket.criticality}`,
                          })}`}
                          active={data.ticket.status === 'NEW'}
                          icon={`${
                            data.ticket.status === 'NEW'
                              ? 'bi bi-exclamation-circle'
                              : 'bi bi-calendar2-check'
                          }`}
                          activeColor={
                            data.ticket.criticality === CriticalityLevel.SEVERE
                              ? 'text-danger'
                              : data.ticket.criticality === CriticalityLevel.HIGH
                              ? 'text-warning'
                              : data.ticket.criticality === CriticalityLevel.MODERATE
                              ? 'text-success'
                              : 'text-primary'
                          }
                        /> */}

                      <IconLabelContent
                        title={
                          (data.ticket.totalAttachments ?? 0) > 0
                            ? intl
                                .formatMessage({
                                  id:
                                    data.ticket.totalAttachments === 1
                                      ? 'TITLE.ATTACHMENT.TOTAL_SINGLE'
                                      : 'TITLE.ATTACHMENT.TOTAL_MULTIPLE',
                                })
                                .replace(/%s/g, `${data.ticket.totalAttachments}`)
                            : intl.formatMessage({id: 'TITLE.ATTACHMENT.ZERO'})
                        }
                        active={(data.ticket.totalAttachments ?? 0) > 0}
                        labelValue={`${data.ticket.totalAttachments || 0}`}
                        icon='bi bi-paperclip'
                      />

                      <IconLabelContent
                        title={
                          (data.ticket.totalTasks ?? 0) > 0
                            ? intl
                                .formatMessage({
                                  id:
                                    data.ticket.totalTasks === 1
                                      ? 'TITLE.TASK.TOTAL_SINGLE'
                                      : 'TITLE.TASK.TOTAL_MULTIPLE',
                                })
                                .replace(/%s/g, `${data.ticket.totalTasks}`)
                            : intl.formatMessage({id: 'TITLE.TASK.ZERO'})
                        }
                        active={(data.ticket.totalTasks ?? 0) > 0}
                        labelValue={`${data.ticket.totalTasks || 0}`}
                        icon='bi bi-stickies'
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className='d-flex flex-stack py-2 bg-light-primary'>
              <div className='d-flex flex-stack'>
                <div className='d-flex align-items-center'>
                  <div className='ms-2'>
                    <div className='fs-7'>NOT ABLE TO LOAD TICKET</div>
                    {error && (
                      <span className='text-danger '>
                        <div className='d-flex flex-row justify-content-start align-items-center'>
                          <span className='fs-8 me-1'>
                            <i className='text-danger me-1 bi bi-exclamation-octagon' />
                            {error}
                          </span>
                        </div>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        }
      ></CustomInputRenderer>
    </>
  )
}
